// Browser client for the GINI Code -> Studio handoff routes.
//
// Talks only to the daemon's own `/api/gini-platform/handoff/*` surface —
// never to GINI Code or a gateway directly, so no third-party origin or
// credential is ever reachable from page JS.
//
// The opaque redemption code is the ONLY handoff value this module ever puts
// in a URL, and it appears solely in the path of a same-origin request. No
// function here accepts a redirect URL, a provider key, or a filesystem
// path, because no route on the other side accepts one either.

import type {
  GiniHandoffArtifactsResponse,
  GiniHandoffReturnResponse,
  GiniHandoffSessionErrorCode,
  GiniHandoffSessionView,
  GiniHandoffStatusProbe,
} from '@open-design/contracts/gini-platform';

const BASE = '/api/gini-platform/handoff';

export interface GiniHandoffClientFailure {
  ok: false;
  code: GiniHandoffSessionErrorCode | 'NETWORK_ERROR';
  message: string;
}

export type GiniHandoffSessionResult = { ok: true; session: GiniHandoffSessionView } | GiniHandoffClientFailure;

async function readFailure(resp: Response): Promise<GiniHandoffClientFailure> {
  const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>;
  return {
    ok: false,
    code: (typeof body.code === 'string' ? body.code : 'HANDOFF_INVALID') as GiniHandoffSessionErrorCode,
    message: typeof body.message === 'string' ? body.message : `Request failed (${resp.status}).`,
  };
}

function networkFailure(err: unknown): GiniHandoffClientFailure {
  return { ok: false, code: 'NETWORK_ERROR', message: err instanceof Error ? err.message : String(err) };
}

/** Unauthenticated probe — safe to call before sign-in. Never returns payload data. */
export async function probeHandoff(code: string, signal?: AbortSignal): Promise<GiniHandoffStatusProbe | null> {
  try {
    const resp = await fetch(`${BASE}/${encodeURIComponent(code)}/status`, { cache: 'no-store', ...(signal ? { signal } : {}) });
    if (!resp.ok) return null;
    return (await resp.json()) as GiniHandoffStatusProbe;
  } catch {
    return null;
  }
}

/**
 * One-time redemption. Deliberately has no retry: a second call against a
 * consumed code is a replay and the server rejects it, so retrying on
 * failure would turn a transient error into a permanently-burned handoff
 * from the user's point of view.
 */
export async function redeemHandoff(code: string): Promise<GiniHandoffSessionResult> {
  try {
    const resp = await fetch(`${BASE}/${encodeURIComponent(code)}/redeem`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!resp.ok) return await readFailure(resp);
    const body = (await resp.json()) as { session: GiniHandoffSessionView };
    return { ok: true, session: body.session };
  } catch (err) {
    return networkFailure(err);
  }
}

export async function fetchHandoffSession(handoffId: string, signal?: AbortSignal): Promise<GiniHandoffSessionResult> {
  try {
    const resp = await fetch(`${BASE}/sessions/${encodeURIComponent(handoffId)}`, {
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    });
    if (!resp.ok) return await readFailure(resp);
    const body = (await resp.json()) as { session: GiniHandoffSessionView };
    return { ok: true, session: body.session };
  } catch (err) {
    return networkFailure(err);
  }
}

export type GiniHandoffArtifactsResult = { ok: true; data: GiniHandoffArtifactsResponse } | GiniHandoffClientFailure;

export async function fetchHandoffArtifacts(handoffId: string, signal?: AbortSignal): Promise<GiniHandoffArtifactsResult> {
  try {
    const resp = await fetch(`${BASE}/sessions/${encodeURIComponent(handoffId)}/artifacts`, {
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    });
    if (!resp.ok) return await readFailure(resp);
    return { ok: true, data: (await resp.json()) as GiniHandoffArtifactsResponse };
  } catch (err) {
    return networkFailure(err);
  }
}

export type GiniHandoffReturnResult = { ok: true; data: GiniHandoffReturnResponse } | GiniHandoffClientFailure;

/**
 * Requests the bounded return reference. Note what is NOT here: no target
 * URL parameter. The destination is resolved server-side from the handoff's
 * contract-governed `returnTarget`, so this call cannot be pointed anywhere.
 */
export async function requestHandoffReturn(
  handoffId: string,
  selection: { artifactId?: string; artifactVersion?: number } = {},
): Promise<GiniHandoffReturnResult> {
  try {
    const resp = await fetch(`${BASE}/sessions/${encodeURIComponent(handoffId)}/return`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(selection),
    });
    if (!resp.ok) return await readFailure(resp);
    return { ok: true, data: (await resp.json()) as GiniHandoffReturnResponse };
  } catch (err) {
    return networkFailure(err);
  }
}

/** Requests a bounded, expiring download reference for one file in a version. */
export async function requestArtifactDownloadReference(
  artifactId: string,
  artifactVersion: number,
  filePath: string,
): Promise<{ ok: true; referenceId: string; expiresAt: string } | GiniHandoffClientFailure> {
  try {
    const resp = await fetch(
      `/api/gini-platform/artifacts/${encodeURIComponent(artifactId)}/versions/${artifactVersion}/download-reference`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ path: filePath }),
      },
    );
    if (!resp.ok) return await readFailure(resp);
    const body = (await resp.json()) as { referenceId: string; expiresAt: string };
    return { ok: true, referenceId: body.referenceId, expiresAt: body.expiresAt };
  } catch (err) {
    return networkFailure(err);
  }
}

/** Reads the handoff code out of `/handoff/:code`. Returns null on any other path. */
export function readHandoffCodeFromPath(pathname: string): string | null {
  const match = /^\/handoff\/([^/?#]+)\/?$/.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] as string);
  } catch {
    return match[1] as string;
  }
}
